import type {ReactElement} from 'react';
import React, {useState} from 'react';
import Slider from '@postermywall/rc-slider';
import 'rc-slider/assets/index.css';
import './audio-item-fade-slider.scss';
import type {AudioItemFadeSliderProps} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/audio-item-fade-slider/audio-item-fade-slider.types';
import {noop} from '@Utils/general.util';
import {FadeDirection} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/fade-background/fade-background.types';
import type {HandleProps} from '@postermywall/rc-slider/lib/Handles/Handle';
import {useAppSelector} from '@/hooks';

export function AudioItemFadeSlider({onClickHandle = noop, fadeDirection = FadeDirection.LEFT, ...props}: AudioItemFadeSliderProps): ReactElement<any> | null {
  const audioItemObject = useAppSelector((state) => {
    return state.posterEditor.posterObject?.audioClips.audioItemsHashMap[props.audioItemId];
  });
  const audioItem = window.posterEditor?.whiteboard?.audioClips.audioItemsHashMap[props.audioItemId];
  const [isMouseDown, setIsMouseDown] = useState(false);

  if (!audioItem || !audioItemObject) {
    return null;
  }

  if (!audioItem.audioPlayer.hasFade()) {
    return null;
  }

  const onFadeInDurationChange = (val: number, undoable: boolean): void => {
    void audioItem.updateFromObject(
      {
        audioPlayer: {
          fade: {
            fadeInDuration: val,
          },
        },
      },
      {
        undoable,
      }
    );
  };

  const onFadeOutDurationChange = (val: number, undoable: boolean): void => {
    void audioItem.updateFromObject(
      {
        audioPlayer: {
          fade: {
            fadeOutDuration: val,
          },
        },
      },
      {
        undoable,
      }
    );
  };

  const onChangeFade = (val: number, undoable = true): void => {
    if (fadeDirection === FadeDirection.LEFT) {
      onFadeInDurationChange(val, undoable);
    } else {
      onFadeOutDurationChange(val, undoable);
    }
  };

  const handleRender = (handleProps: ReactElement<HandleProps, string | React.JSXElementConstructor<any>>): ReactElement<any> => {
    return (
      <div
        onPointerDown={(event): void => {
          setIsMouseDown(true);
          onClickHandle();
          event.stopPropagation();
        }}
        onPointerUp={(event): void => {
          setIsMouseDown(false);
          event.stopPropagation();
        }}
      >
        {handleProps}
      </div>
    );
  };

  return (
    <Slider
      handleRender={handleRender}
      className={`${fadeDirection === FadeDirection.RIGHT ? 'reverse' : ''} audio-item-fade-slider`}
      min={0}
      reverse={fadeDirection === FadeDirection.RIGHT}
      step={0.01}
      max={audioItem.audioPlayer.getMaxFadeDuration()}
      value={fadeDirection === FadeDirection.RIGHT ? audioItemObject.audioPlayer.fade.fadeOutDuration : audioItemObject.audioPlayer.fade.fadeInDuration}
      onChange={(val) => {
        if (isMouseDown) {
          onChangeFade(val as number, false);
        }
      }}
      onAfterChange={(val) => {
        if (isMouseDown) {
          onChangeFade(val as number, true);
        }
      }}
    />
  );
}
